<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('adminStatisticsDashboard.cards.byContracts') }}
    </v-toolbar>

    <v-sheet color="grey lighten-3"
             class="px-4 pt-4 pb-3"
             tile>
      <h3 class="transition-swing font-weight-medium">{{ $t('statisticsTable.filterTitle') }}</h3>
      <v-responsive>
        <v-row>
          <v-col cols="12" sm="6" md="3" class="py-2">
            <v-autocomplete v-model="filters.globalUnitId"
                            :items="globalUnits"
                            :label="$t('statisticsTable.labels.organization')"
                            :dense="true"
                            item-text="Name"
                            item-value="Id"
                            :clearable="false"
                            :rules="[v => !!v || 'Required']"
                            @change="load"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3" class="py-2">
            <v-autocomplete v-model="filters.regionId"
                            :items="regions"
                            label="Region"
                            :dense="true"
                            item-text="Name"
                            item-value="Id"
                            :clearable="true"
                            @change="load"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2" class="py-2">
            <DateInput v-model="filters.start"
                       :label="$t('statisticsTable.labels.startDate')"
                       :dense="true"
                       min-width="0"
                       max-width="80%"
                       :clearable="false"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2" class="py-2">
            <DateInput v-model="filters.end"
                       :label="$t('statisticsTable.labels.endDate')"
                       :dense="true"
                       min-width="0"
                       max-width="80%"
                       :clearable="false"
            />
          </v-col>

          <v-col cols="12" sm="12" md="2" class="mt-auto mb-auto d-flex justify-end">
            <v-btn large color="primary" @click="load" class="mr-2">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn large color="warning" dark @click="onExcelButtonClicked">
              <v-icon>mdi-file-excel</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-responsive>
    </v-sheet>

    <v-card-text>
      <v-simple-table :dense="true" class="bordered-table">
        <template v-slot:default>
          <thead>
          <tr>
            <th class="text-center font-weight-black">#</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.inn') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.organization') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.unit') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.contractNumber') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.contractDate') }}</th>

            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.servicedArea') }}</th>

            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.contractBranch') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.acceptedForPeriod') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.over3Sheets') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.pageCount') }}</th>

            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.price') }} <br>
              <span style="font-size: 0.8em;">{{ $t('statisticsTable.headers.regularMail') }}</span>
            </th>
            <th class="text-center font-weight-black">
              {{ $t('statisticsTable.headers.price') }} <br>
              <span style="font-size: 0.8em;">{{ $t('statisticsTable.headers.over3Sheets') }}</span>
            </th>

            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.sumForRegular') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.sumForOver3Sheets') }}</th>
            <th class="text-center font-weight-black">{{ $t('statisticsTable.headers.totalSum') }}</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="stats.length">
            <tr v-for="(item, index) in stats" :key="index" :class="{'group-separator': item.isLastInGroup}">
              <td class="text-center">{{ index + 1 }}</td>

              <td v-if="item.showOrg"
                  :rowspan="item.orgRowSpan"
                  class="text-center white merged-cell-border"
                  style="vertical-align: middle;">
                {{ item.Inn || '-' }}
              </td>

              <td v-if="item.showOrg"
                  :rowspan="item.orgRowSpan"
                  class="text-left white merged-cell-border"
                  style="vertical-align: middle;">
                <div class="font-weight-medium">{{ item.OrganizationName }}</div>
              </td>

              <td class="text-left">{{ item.UnitName }}</td>
              <td class="text-center">
                <div v-if="item.ContractNumber" class="font-weight-medium">{{ item.ContractNumber }}</div>
                <div v-else>-</div>
              </td>
              <td class="text-center">
                <div v-if="item.ContractDate">{{ item.ContractDate }}</div>
                <div v-else>-</div>
              </td>

              <td class="text-left">
                {{ selectedRegionName }}
              </td>

              <td class="text-left"><small>{{ item.ContractRegions || '-' }}</small></td>
              <td class="text-right">{{ item.AcceptedCount | decimalNoTrim }}</td>
              <td class="text-right">{{ item.Over3SheetsCount | decimalNoTrim }}</td>
              <td class="text-center">{{ item.TotalPages | decimalNoTrim }}</td>

              <td class="text-right">{{ item.PriceNormal | decimalNoTrim }}</td>
              <td class="text-right">{{ item.PriceOver | decimalNoTrim }}</td>

              <td class="text-right">{{ item.SumNormal | decimalNoTrim }}</td>
              <td class="text-right">{{ item.SumOver | decimalNoTrim }}</td>
              <td class="text-right font-weight-bold">{{ item.TotalSum | decimalNoTrim }}</td>
            </tr>

            <tr class="blue lighten-5">
              <td class="text-center font-weight-black" colspan="8">{{ $t('statisticsTable.footer.total') }}</td>
              <td class="text-right font-weight-black">{{ totalStats.AcceptedCount | decimalNoTrim }}</td>
              <td class="text-right font-weight-black">{{ totalStats.Over3SheetsCount | decimalNoTrim }}</td>
              <td class="text-center font-weight-black">{{ totalStats.TotalPages | decimalNoTrim }}</td>

              <td class="text-right font-weight-black"></td>
              <td class="text-right font-weight-black"></td>

              <td class="text-right font-weight-black">{{ totalStats.SumNormal | decimalNoTrim }}</td>
              <td class="text-right font-weight-black">{{ totalStats.SumOver | decimalNoTrim }}</td>
              <td class="text-right font-weight-black">{{ totalStats.TotalSum | decimalNoTrim}}</td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="16" class="text-center">{{ $t('statisticsTable.noData') }}</td>
            </tr>
          </template>

          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP, LAYOUT_TITLE_CHANGED} from "@/store/modules/layout/actions";
import StatisticsService from "@/services/statistics.service";
import _ from "underscore";
import DateInput from "@/components/input/DateInput";
import Moment from 'moment'
import XLSX from "xlsx";
import {PRICE_SIMPLE_FOR_HYBRID, PRICE_OVER_PAGE_FOR_HYBRID} from "@/contants/constants";

export default {
  name: "ContractStatisticPage",
  components: {
    DateInput
  },
  data() {
    return {
      globalUnits: [],
      regions: [],
      stats: [],
      totalStats: {
        AcceptedCount: 0,
        Over3SheetsCount: 0,
        TotalPages: 0,
        SumNormal: 0,
        SumOver: 0,
        TotalSum: 0
      },
      filters: {
        globalUnitId: null,
        regionId: null,
        start: Moment(),
        end: Moment(),
      }
    }
  },
  computed: {
    selectedGlobalUnitName() {
      if (!this.filters.globalUnitId || !this.globalUnits.length) return '';
      const unit = this.globalUnits.find(u => u.Id === this.filters.globalUnitId);
      return unit ? unit.Name : '';
    },
    selectedRegionName() {
      if (!this.filters.regionId || !this.regions.length) return '-';
      const region = this.regions.find(r => r.Id === this.filters.regionId);
      return region ? region.Name : '-';
    }
  },
  methods: {
    formatNumberWithSpaces(number) {
      if (number === undefined || number === null) return '0';
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    onExcelButtonClicked(){
      this.$store.dispatch(LAYOUT_LOADING_START);

      const workbook = XLSX.utils.book_new();

      // 1. Generate Title Row
      const startStr = this.filters.start ? this.filters.start.format('DD.MM.YYYY') : '';
      const endStr = this.filters.end ? this.filters.end.format('DD.MM.YYYY') : '';
      const titleText = `Статистика по приёму писем от "${this.selectedGlobalUnitName}" на период с ${startStr} по ${endStr}`;
      const titleRow = [titleText];

      // 2. Generate Header Row
      const header = [
        this.$t('statisticsTable.headers.number'),
        this.$t('statisticsTable.headers.inn'),
        this.$t('statisticsTable.headers.organization'),
        this.$t('statisticsTable.headers.unit'),
        this.$t('statisticsTable.headers.contractNumber'),
        this.$t('statisticsTable.headers.contractDate'),
        this.$t('statisticsTable.headers.servicedArea'), // New Column
        this.$t('statisticsTable.headers.contractBranch'),
        this.$t('statisticsTable.headers.acceptedForPeriod'),
        this.$t('statisticsTable.headers.over3Sheets'),
        this.$t('statisticsTable.headers.pageCount'),
        `${this.$t('statisticsTable.headers.price')} (${this.$t('statisticsTable.headers.regularMail')})`,
        `${this.$t('statisticsTable.headers.price')} (${this.$t('statisticsTable.headers.over3Sheets')})`,
        this.$t('statisticsTable.headers.sumForRegular'),
        this.$t('statisticsTable.headers.sumForOver3Sheets'),
        this.$t('statisticsTable.headers.totalSum')
      ];

      // Create sheet with Title and Header
      const worksheet = XLSX.utils.aoa_to_sheet([titleRow, [], header]);

      // Set Column Widths
      worksheet['!cols'] = [
        { width: 5 }, { width: 15 }, { width: 35 }, { width: 20 },
        { width: 15 }, { width: 15 }, { width: 25 }, { width: 25 },
        { width: 15 }, { width: 15 }, { width: 15 },
        { width: 15 }, { width: 15 },
        { width: 20 }, { width: 20 }, { width: 20 }
      ];

      // Add Data
      // Starting from row 4 because: Row 1=Title, Row 2=Empty, Row 3=Header
      // XLSX origin uses 0-based index, so index -1 appends to the end
      this.stats.forEach((item, index) => {
        const row = [
          index + 1,
          item.Inn,
          item.OrganizationName,
          item.UnitName,
          item.ContractNumber,
          item.ContractDate,
          this.selectedRegionName, // Serviced Area
          item.ContractRegions,
          item.AcceptedCount,
          item.Over3SheetsCount,
          item.TotalPages,
          item.PriceNormal,
          item.PriceOver,
          item.SumNormal,
          item.SumOver,
          item.TotalSum
        ];
        XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: -1 });
      });

      // Add Footer
      const footerRow = [
        '', this.$t('statisticsTable.footer.total'), '', '', '', '', '', '',
        this.totalStats.AcceptedCount,
        this.totalStats.Over3SheetsCount,
        this.totalStats.TotalPages,
        '', '',
        this.totalStats.SumNormal,
        this.totalStats.SumOver,
        this.totalStats.TotalSum
      ];
      XLSX.utils.sheet_add_aoa(worksheet, [footerRow], { origin: -1 });

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contract Report');
      XLSX.writeFile(workbook, 'ContractStatistics.xlsx');

      this.$store.dispatch(LAYOUT_LOADING_STOP);
    },

    load() {
      if (!this.filters.globalUnitId) return;

      this.$store.dispatch(LAYOUT_LOADING_START)

      this.totalStats = {
        AcceptedCount: 0, Over3SheetsCount: 0, TotalPages: 0,
        SumNormal: 0, SumOver: 0, TotalSum: 0
      };

      StatisticsService.getContractStatistics({
        globalUnitId: this.filters.globalUnitId,
        regionId: this.filters.regionId,
        start: this.filters.start,
        end: this.filters.end
      })
          .then(response => {
            // 1. Process Data & Assign Constants
            this.stats = _.map(response.data, item => {
              item.PriceNormal = PRICE_SIMPLE_FOR_HYBRID;
              item.PriceOver = PRICE_OVER_PAGE_FOR_HYBRID;
              return item;
            });

            // 2. RowSpan Logic & Group Separation
            let parentItem = null;
            let prevItem = null;

            this.stats.forEach((item, index) => {
              item.orgRowSpan = 1;
              item.showOrg = true;
              item.isLastInGroup = false;

              if (prevItem &&
                  item.OrganizationName === prevItem.OrganizationName &&
                  item.Inn === prevItem.Inn) {

                item.showOrg = false;
                parentItem.orgRowSpan++;
              } else {
                parentItem = item;
                if (prevItem) prevItem.isLastInGroup = true;
              }
              prevItem = item;

              if (index === this.stats.length - 1) {
                item.isLastInGroup = true;
              }
            });

            // 3. Totals Calculation
            _.each(this.stats, s => {
              this.totalStats.AcceptedCount += (s.AcceptedCount || 0);
              this.totalStats.Over3SheetsCount += (s.Over3SheetsCount || 0);
              this.totalStats.TotalPages += (s.TotalPages || 0);
              this.totalStats.SumNormal += (s.SumNormal || 0);
              this.totalStats.SumOver += (s.SumOver || 0);
              this.totalStats.TotalSum += (s.TotalSum || 0);
            });
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },

    loadFilters() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      Promise.all([
        StatisticsService.getGlobalUnits(),
        StatisticsService.getRegions()
      ]).then(([unitRes, regionRes]) => {
        this.globalUnits = unitRes.data;
        this.regions = regionRes.data;

        if (this.globalUnits && this.globalUnits.length > 0) {
          this.filters.globalUnitId = this.globalUnits[0].Id;
        }

        // Auto-select is optional.
        if (this.regions && this.regions.length > 0) {
          this.filters.regionId = this.regions[0].Id;
        }

        this.load();

      }).finally(() => {
        this.$store.dispatch(LAYOUT_LOADING_STOP)
      });
    }
  },
  created() {
    this.$store.dispatch(LAYOUT_TITLE_CHANGED, this.$t('adminStatisticsDashboard.cards.byContracts'))
    this.loadFilters();
  }
}
</script>

<style scoped>
/* Standard Grid Borders */
td, th {
  border: thin solid rgba(0, 0, 0, 0.12);
  font-size: 13px;
}

/* White background for merged cells */
.white {
  background-color: white !important;
}

/* Thicker bottom border for the end of a group */
.group-separator td {
  border-bottom: 2px solid #757575 !important;
}

/* Ensure merged cells also get the border */
.merged-cell-border {
  border-bottom: 2px solid #757575 !important;
}
</style>